import { observer } from 'mobx-react'

import { useCallback, useEffect, useRef, useState } from 'react'
import type { ChangeEvent } from 'react'

import { SearchInput } from '../../Input/Input'
import type { SearchFieldFilter } from '../../../models/common/Filters/SearchFieldFilterModel'

import type { TableFilterComponentProps } from './types'
import useDebounceCallback from 'hooks/useDebounceCallback'

const Search: React.FC<TableFilterComponentProps<SearchFieldFilter>> = ({
  fieldName,
  fieldFilter,
  onFiltersChanged,
  debounceMs = null,
  ...rest
}) => {
  const lastSearchedValue = useRef<string | null>(null)
  const [searchValue, setSearchValue] = useState<string>(fieldFilter.value ?? '')

  const onSearch = () => {
    if (fieldFilter.value !== lastSearchedValue.current) {
      lastSearchedValue.current = fieldFilter.value
    }
  }

  useEffect(() => {
    setSearchValue(fieldFilter.value ?? '')
  }, [fieldFilter.value])

  const onSearchChange = useCallback((value: string) => {
    fieldFilter.setValue(value)
    onFiltersChanged?.(value)
  }, [onFiltersChanged])

  const setSearchFilter = debounceMs ? 
    useDebounceCallback(onSearchChange, debounceMs) : 
    onSearchChange

  return <SearchInput
    name={fieldName}
    title={fieldName}
    showLabel={false}
    value={searchValue}
    placeholder={fieldFilter.placeholder}
    onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setSearchValue(value)
      setSearchFilter(value)
    }}
    onSearch={onSearch}
    onSearchCleared={() => {
      fieldFilter.setValue(null)
      onFiltersChanged?.(null)
    }}
    {...rest}
  />
}

export default observer(Search);